import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { CaseClientStatus } from "generated/graphql";
import { pluralize } from "helpers/coll";
import LoadingSpinner from "./LoadingSpinner";
import { useState } from "react";
import { useSnackbar } from "notistack";

export function BulkCaseClientStatusUpdate({
  open,
  close,
  casesTotal,
  onUpdate,
}: {
  open: boolean;
  close: () => void;
  casesTotal: number;
  onUpdate: (status: CaseClientStatus) => Promise<void>;
}) {
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState<CaseClientStatus>(CaseClientStatus.Active);
  const { enqueueSnackbar } = useSnackbar();

  const handleUpdate = async () => {
    setUpdating(true);

    try {
      await onUpdate(status);
      enqueueSnackbar("Cases updated", { variant: "success" });
      close();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to update cases", { variant: "error" });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogContent className="flex flex-col gap-4">
        <p className="font-bold">Change Active Status</p>
        <FormControl>
          <FormLabel>Choose one:</FormLabel>
          <RadioGroup
            value={status}
            onChange={(e) => setStatus(e.target.value as CaseClientStatus)}
            row
            name="row-radio-buttons-group">
            <FormControlLabel value={CaseClientStatus.Active} control={<Radio />} label="Active" />
            <FormControlLabel value={CaseClientStatus.Inactive} control={<Radio />} label="Inactive" />
          </RadioGroup>
        </FormControl>
        <p className="text-sm">
          You are about to change {casesTotal.toLocaleString()} {pluralize(casesTotal, "case")} to:{" "}
          <strong className="capitalize">{status}</strong>
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="default">
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="default"
          disabled={updating}
          onClick={handleUpdate}
          startIcon={updating ? <LoadingSpinner size={24} /> : <></>}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
